/**
 * eHungry Locations — which RushMyFood restaurant each eHungry restaurant is.
 *
 * This screen is what makes the mirrored orders reach anyone. Until a location
 * is linked, its orders are admin-only: they appear on the Dispatch board's
 * eHungry tab, but the restaurant's own Orders page and tablet show nothing.
 * That is the safe default — a wrong link would show one restaurant another's
 * customers, so the sync only auto-links on an unambiguous phone match and
 * leaves everything else here for a person to confirm.
 *
 * Linking also back-fills: the mutation re-stamps that location's existing
 * orders, so history appears immediately rather than only orders that arrive
 * afterwards.
 */
import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Chip, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, Select, MenuItem, TextField,
  Stack, Button, InputAdornment,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'

import Header from '../components/Headers/Header'
import { restaurants as RESTAURANTS_QUERY } from '../apollo'

const GET_RESTAURANTS = gql`${RESTAURANTS_QUERY}`

const EHUNGRY_LOCATIONS = gql`
  query AdminEhungryLocations {
    adminEhungryLocations {
      _id phoneId ehungryId name phone address city state zip
      restaurantId restaurantName autoLinked orderCount lastOrderAt
    }
  }
`

const SYNC_STATUS = gql`
  query AdminEhungrySyncStatus {
    adminEhungrySyncStatus
  }
`

const LINK_LOCATION = gql`
  mutation EhungryLinkLocation($phoneId: String!, $restaurantId: ID) {
    ehungryLinkLocation(phoneId: $phoneId, restaurantId: $restaurantId) {
      _id phoneId restaurantId restaurantName autoLinked
    }
  }
`

const when = (s) => (s ? new Date(s).toLocaleDateString() : '—')

const EhungryLocations = () => {
  const [search, setSearch] = useState('')
  const [snackbar, setSnackbar] = useState(null)

  const { data, loading, error, refetch } = useQuery(EHUNGRY_LOCATIONS, { fetchPolicy: 'cache-and-network' })
  const { data: statusData, refetch: refetchStatus } = useQuery(SYNC_STATUS, { fetchPolicy: 'cache-and-network' })
  const { data: restaurantData } = useQuery(GET_RESTAURANTS)
  const [linkLocation, linkState] = useMutation(LINK_LOCATION)

  const locations = data?.adminEhungryLocations ?? []
  const restaurants = restaurantData?.restaurants ?? []

  const sync = useMemo(() => {
    try { return statusData?.adminEhungrySyncStatus ? JSON.parse(statusData.adminEhungrySyncStatus) : null }
    catch { return null }
  }, [statusData])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return locations
    return locations.filter((l) => [l.name, l.phone, l.phoneId, l.city, l.restaurantName]
      .some((v) => String(v || '').toLowerCase().includes(term)))
  }, [locations, search])

  const linkedCount = locations.filter((l) => l.restaurantId).length

  const handleLink = async (location, restaurantId) => {
    try {
      await linkLocation({
        variables: { phoneId: location.phoneId, restaurantId: restaurantId || null },
      })
      setSnackbar({
        severity: 'success',
        message: restaurantId
          ? `${location.name || location.phoneId} linked — its ${location.orderCount} order(s) now show for that restaurant`
          : `${location.name || location.phoneId} unlinked`,
      })
      refetch()
    } catch (e) {
      setSnackbar({ severity: 'error', message: e.message })
    }
  }

  return (
    <>
      <Header />
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>eHungry Locations</Typography>
            <Typography variant="body2" color="text.secondary">
              Link each eHungry restaurant to a RushMyFood restaurant so its orders show on that restaurant&apos;s Orders page and tablet.
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => { refetch(); refetchStatus() }}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Stack>

        {/* The feed's own state. A stalled sync otherwise looks exactly like a
            quiet night, which is how a missing day of orders goes unnoticed. */}
        {sync && (
          <Alert
            severity={sync.enabled ? (sync.lastError || sync.persistedError ? 'warning' : 'info') : 'warning'}
            sx={{ mb: 2 }}
          >
            {!sync.configured
              ? 'Sync is not configured — set EHUNGRY_SYNC_URL and EHUNGRY_SYNC_TOKEN on the backend.'
              : !sync.enabled
              ? 'Sync is switched off — set EHUNGRY_SYNC_ENABLED=true on the backend.'
              : `Sync running. ${sync.ordersSynced || 0} order(s) pulled, last success ${sync.lastSuccessAt ? new Date(sync.lastSuccessAt).toLocaleString() : 'never'}.`}
            {(sync.lastError || sync.persistedError) && ` Last error: ${sync.lastError || sync.persistedError}`}
          </Alert>
        )}

        <Stack direction="row" gap={2} alignItems="center" mb={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search name, phone or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 280 }}
          />
          <Chip label={`${locations.length} eHungry locations`} size="small" />
          <Chip
            label={`${linkedCount} linked`}
            size="small"
            sx={{ bgcolor: linkedCount ? '#dcfce7' : '#f3f4f6', color: linkedCount ? '#166534' : '#374151', fontWeight: 600 }}
          />
          {locations.length - linkedCount > 0 && (
            <Chip
              label={`${locations.length - linkedCount} not linked`}
              size="small"
              sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
            />
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

        <Paper sx={{ overflowX: 'auto' }}>
          {loading && locations.length === 0 ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 700 }}>eHungry restaurant</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Orders</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last order</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>RushMyFood restaurant</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        {locations.length === 0
                          ? 'No eHungry locations yet — they appear after the first sync runs.'
                          : 'No locations match that search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((loc) => (
                  <TableRow key={loc._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{loc.name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">id {loc.phoneId}</Typography>
                    </TableCell>
                    <TableCell>{loc.phone || '—'}</TableCell>
                    <TableCell>{[loc.city, loc.state].filter(Boolean).join(', ') || '—'}</TableCell>
                    <TableCell align="right">{loc.orderCount}</TableCell>
                    <TableCell>{when(loc.lastOrderAt)}</TableCell>
                    <TableCell>
                      <Stack direction="row" gap={1} alignItems="center">
                        <Select
                          size="small"
                          displayEmpty
                          value={loc.restaurantId || ''}
                          disabled={linkState.loading}
                          onChange={(e) => handleLink(loc, e.target.value)}
                          sx={{ minWidth: 200 }}
                        >
                          <MenuItem value="">
                            <em>Not linked</em>
                          </MenuItem>
                          {restaurants.map((r) => (
                            <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
                          ))}
                        </Select>
                        {/* Worth distinguishing: an auto-link was a phone match,
                            not somebody's decision, so it is the one to check. */}
                        {loc.restaurantId && loc.autoLinked && (
                          <Chip label="auto" size="small" sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 600 }} />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>{snackbar.message}</Alert> : null}
      </Snackbar>
    </>
  )
}

export default EhungryLocations
